/**
 * Язык интерфейса и языковые адреса.
 *
 * Русский — исходный язык сайта и живёт по адресам без префикса: они уже
 * проиндексированы и разошлись по ссылкам, менять их было нельзя. Переводы
 * получают префикс: /en/blog/slug, /zh/blog/slug.
 *
 * Язык берётся из адреса, а не из настройки в localStorage. Это принципиально:
 * у каждой языковой версии должен быть свой URL, иначе поисковику нечего
 * индексировать, а поделиться ссылкой на английский текст невозможно.
 */

import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export type LanguageCode = 'ru' | 'en' | 'zh'

export const DEFAULT_LANGUAGE: LanguageCode = 'ru'
export const TRANSLATED_LANGUAGES: LanguageCode[] = ['en', 'zh']
export const ALL_LANGUAGES: LanguageCode[] = ['ru', 'en', 'zh']

export const LANGUAGE_NAMES: Record<LanguageCode, string> = {
  ru: 'Русский',
  en: 'English',
  zh: '中文',
}

/** Короткая подпись для переключателя — в шапке места мало. */
export const LANGUAGE_SHORT: Record<LanguageCode, string> = {
  ru: 'RU',
  en: 'EN',
  zh: '中',
}

export const LANGUAGE_LOCALES: Record<LanguageCode, string> = {
  ru: 'ru_RU',
  en: 'en_US',
  zh: 'zh_CN',
}

/** Ключ, по которому запоминается выбор — только для первого захода на «/». */
const STORAGE_KEY = 'dustar:lang'

export function isLanguage(value: string): value is LanguageCode {
  return (ALL_LANGUAGES as string[]).includes(value)
}

/** Отделяет языковой префикс: «/en/blog/x» → ['en', '/blog/x']. */
export function splitPath(path: string): [LanguageCode, string] {
  const segments = path.split('/').filter(Boolean)
  const head = segments[0]

  if (head && isLanguage(head) && head !== DEFAULT_LANGUAGE) {
    const rest = '/' + segments.slice(1).join('/')
    return [head, rest === '/' ? '/' : rest]
  }

  return [DEFAULT_LANGUAGE, path || '/']
}

/** Приставляет префикс к пути. Русский остаётся без него. */
export function withLanguage(language: LanguageCode, path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`
  return language === DEFAULT_LANGUAGE ? clean : `/${language}${clean}`
}

export interface UseLanguageResult {
  current: ComputedRef<LanguageCode>
  /** Путь без языкового префикса — по нему строятся ссылки внутри сайта. */
  basePath: ComputedRef<string>
  /** Ссылка на этот же экран на другом языке. */
  pathFor: (language: LanguageCode) => string
  switchTo: (language: LanguageCode) => void
  /** Локализует внутреннюю ссылку под текущий язык. */
  localize: (path: string) => string
}

export function useLanguage(): UseLanguageResult {
  const route = useRoute()
  const router = useRouter()

  const current = computed<LanguageCode>(() => splitPath(route.path)[0])
  const basePath = computed(() => splitPath(route.path)[1])

  /**
   * Куда вести переключатель.
   *
   * У статьи на каждом языке свой slug, и подставить префикс к текущему адресу
   * нельзя — получится несуществующая страница. Точные адреса приходят с
   * бэкенда в `alternates` и кладутся в meta маршрута; здесь — запасной путь
   * для страниц без переводов (списки, разделы).
   */
  function pathFor(language: LanguageCode): string {
    const alternates = route.meta.alternates as Record<string, string> | undefined
    if (alternates?.[language]) return alternates[language]

    return withLanguage(language, basePath.value)
  }

  function switchTo(language: LanguageCode): void {
    try {
      localStorage.setItem(STORAGE_KEY, language)
    } catch {
      // Приватный режим — выбор просто не запомнится, это не повод падать
    }
    router.push(pathFor(language))
  }

  return {
    current,
    basePath,
    pathFor,
    switchTo,
    localize: (path: string) => withLanguage(current.value, path),
  }
}

/**
 * Язык, который стоит предложить при первом заходе на корень.
 *
 * Только предложить: сам по себе редирект по языку браузера вреден — робот
 * приходит с англоязычным Accept-Language и получил бы английскую версию
 * вместо русского оригинала. Поэтому используется лишь при явном заходе
 * человека на «/» без сохранённого выбора, и это решает вызывающий код.
 */
export function preferredLanguage(): LanguageCode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && isLanguage(saved)) return saved
  } catch {
    // localStorage недоступен — падать не на чем
  }

  const nav = navigator.language?.toLowerCase() ?? ''
  if (nav.startsWith('zh')) return 'zh'
  if (nav.startsWith('en')) return 'en'
  return DEFAULT_LANGUAGE
}
